/* eslint-disable react/jsx-props-no-spreading */
/**
 * Props spreading is required here because this component extends React.ButtonHTMLAttributes
 * and needs to forward all standard button attributes (like aria-* attributes, data-* attributes,
 * and event handlers) to the underlying button element. This is essential for accessibility
 * and proper event handling in the tree structure.
 */
import React from 'react';
import clsx from 'clsx';
import { Collapse, Dropdown } from 'react-bootstrap';
import { useFileExplorerFolder } from '../../hooks/useFileExplorerNode';
import { TreeFolder } from './services';
import { PathNode } from './Context/FileExplorerContext';
import { useFileExplorerContext } from '../../hooks/useFileExplorerContext';
import { DropdownCustomToggle } from '../common/Dropdown';
import {
  EllipsisIcon, FolderIcon,
  RenameIcon,
  TrashIcon,
} from './FileExplorerIcons';
import { useControlledModal } from '../../hooks/useControlledModal';
import { FileExplorerVisualizerActions } from '../../types/fileExplorerTypes';
import { EditFolderModal } from './Modals/EditFolderModal';
import { DeleteFolderModal } from './Modals/DeleteFolderModal';

// Component interfaces
interface FolderToggleButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isActive: boolean;
  isExpanded: boolean;
  hideArrow: boolean;
  userIsSuperuser: boolean;
  isLoading: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

interface FolderChildrenProps {
  folders?: TreeFolder[];
  isExpanded: boolean;
  parentPath?: PathNode[];
}

// This interface defines the props for the FileExplorerTreeNode component.
// Extends React.ButtonHTMLAttributes<HTMLButtonElement> to allow all standard button attributes.
interface FileExplorerTreeNodeProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  data: TreeFolder;
  parentPath?: PathNode[];
}

// Reusable Components
function FolderToggleButton({
  isActive,
  isExpanded,
  hideArrow,
  userIsSuperuser,
  isLoading,
  onToggle,
  children,
  ...buttonProps
}: FolderToggleButtonProps) {
  return (
    <button
      className="file-explorer-list-navigation-button__content"
      onClick={onToggle}
      type="button"
      {...buttonProps}
    >
      <span className="file-explorer-list-navigation-button__content__icons">
        <span className={clsx('file-explorer-list-navigation-button__content__icons-arrow', {
          expanded: isExpanded,
          hidden: hideArrow || !userIsSuperuser,
        })}
        />
        <span className="file-explorer-list-navigation-button__content__icons-folder" />
      </span>

      <span className="file-explorer-list-navigation-button__content-label">
        {children}
      </span>
    </button>
  );
}

function FolderActionsDropdown({ folder }: { folder: TreeFolder }) {
  const { currentAction, setCurrentAction } = useControlledModal<FileExplorerVisualizerActions>();
  return (
    <>
      <Dropdown className="">
        <Dropdown.Toggle
          as={DropdownCustomToggle}
          className="file-explorer-dropdown"
          id="file-explorer-folder-actions-dropdown-toggle"
        >
          <EllipsisIcon className="rotate-90" />
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Item
            eventKey="folder.share"
            onClick={() => {
              window.open(folder.shareable_link, '_blank');
            }}
          >
            <FolderIcon />
            Ver carpeta
          </Dropdown.Item>
          <Dropdown.Item
            eventKey="folder.edit"
            onClick={() => setCurrentAction('folder.edit')}
          >
            <RenameIcon />
            Editar
          </Dropdown.Item>
          <Dropdown.Item
            eventKey="folder.delete"
            onClick={() => setCurrentAction('folder.delete')}
          >
            <TrashIcon />
            Eliminar
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
      <EditFolderModal
        show={currentAction === 'folder.edit'}
        onHide={() => setCurrentAction(null)}
        data={folder}
      />
      <DeleteFolderModal
        show={currentAction === 'folder.delete'}
        onHide={() => setCurrentAction(null)}
        data={folder}
      />
    </>
  );
}

function FolderChildren({
  folders,
  isExpanded,
  parentPath,
}: FolderChildrenProps) {
  return (
    <Collapse in={isExpanded}>
      <div className="file-explorer-content">
        {folders?.map((folder: TreeFolder) => (
          <li key={folder.id}>
            <FileExplorerTreeNode
              data={folder}
              parentPath={parentPath}
            />
          </li>
        ))}
      </div>
    </Collapse>
  );
}

export function FileExplorerTreeNode(props: FileExplorerTreeNodeProps) {
  const {
    data,
    parentPath,
  } = props;

  const {

    id, name, permissions, description, remove_file_prefix, shareable_link,
  } = data;

  // Use the custom hook to manage folder state and interactions
  // This hook handles the expansion, toggling, and fetching of folder data
  const {
    isExpanded,
    handleToggle,
    folders,
    isFetched,
    isLoading,
    isActive,
  } = useFileExplorerFolder({
    id, name, parentPath, permissions, description, remove_file_prefix, shareable_link,
  });
  const { userIsSuperuser } = useFileExplorerContext();

  const hasChildren = Boolean(folders?.length);
  const hideArrow = isFetched && !hasChildren;
  const shouldShowExpanded = isExpanded && isFetched && hasChildren;

  return (
    <li className="file-explorer-list__item">
      <div
        className={clsx(
          'file-explorer-list-navigation-button btn-group align-items-center w-100',
          {
            'file-explorer-list-navigation-button--active': isActive,
          },
        )}
      >
        <FolderToggleButton
          isActive={isActive}
          isExpanded={shouldShowExpanded}
          hideArrow={hideArrow}
          userIsSuperuser={userIsSuperuser}
          isLoading={isLoading}
          onToggle={handleToggle}
          aria-expanded={shouldShowExpanded}
          data-active={isActive}
        >
          {name}
        </FolderToggleButton>
        <FolderActionsDropdown folder={data} />
      </div>

      <FolderChildren
        folders={folders}
        isExpanded={shouldShowExpanded}
        parentPath={[...(parentPath ?? []), { id, name }]}
      />
    </li>
  );
}
